/**
 * 확장 가능한 라우팅 설정
 * 정석 방법론: 설정 기반의 타입 안전한 라우팅 시스템
 */
import { I18N_LANG_KEY } from '@edusense/shared-constants';

// 지원 언어 설정
export const SUPPORTED_LANGUAGES = ['ko', 'en', 'ja'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

// 기본 언어 설정
export const DEFAULT_LANGUAGE: SupportedLanguage = 'ko';

// 브라우저 언어 매핑
export const BROWSER_LANGUAGE_MAP: Record<string, SupportedLanguage> = {
  'ko': 'ko',
  'ko-KR': 'ko',
  'en': 'en', 
  'en-US': 'en',
  'en-GB': 'en',
  'ja': 'ja',
  'ja-JP': 'ja',
};

// 라우트 정의
export interface RouteDefinition {
  path: string;
  requiresAuth?: boolean;
  requiresMainLayout?: boolean;
  component: string; // 컴포넌트 참조용
}

// 애플리케이션 라우트 맵
export const APP_ROUTES: Record<string, RouteDefinition> = {
  // 인증 없이 접근 가능한 페이지
  landing: {
    path: 'landing',
    requiresAuth: false,
    requiresMainLayout: false,
    component: 'LandingPage'
  },
  
  // 메인 레이아웃이 필요한 인증된 페이지들
  feed: {
    path: 'feed', 
    requiresAuth: true,
    requiresMainLayout: true,
    component: 'MainPage'
  },
  discover: {
    path: 'discover',
    requiresAuth: true, 
    requiresMainLayout: true,
    component: 'DiscoverPageComponent'
  },
  learn: {
    path: 'learn',
    requiresAuth: true,
    requiresMainLayout: true, 
    component: 'CoursesPage'
  },
  courses: {
    path: 'courses',
    requiresAuth: true,
    requiresMainLayout: true,
    component: 'CoursesPage'
  },
  profile: {
    path: 'profile',
    requiresAuth: true,
    requiresMainLayout: true,
    component: 'ProfilePage'
  },
  'profile/:userId': {
    path: 'profile/:userId',
    requiresAuth: true,
    requiresMainLayout: true,
    component: 'ProfilePage'
  },
  settings: {
    path: 'settings',
    requiresAuth: true,
    requiresMainLayout: true,
    component: 'SettingsPageWrapper'
  },
  'project/:projectId': {
    path: 'project/:projectId',
    requiresAuth: true,
    requiresMainLayout: true,
    component: 'ProjectDetailPage'
  },
  'project/new': {
    path: 'project/new',
    requiresAuth: true,
    requiresMainLayout: true,
    component: 'ProjectUploadPage'
  }
};

// 특수 라우트 설정
export const SPECIAL_ROUTES = {
  oauth2Redirect: '/oauth2/redirect',
  root: '/',
} as const;

// 리다이렉트 규칙
export interface RedirectRule {
  from: string | RegExp;
  to: (params: any) => string;
  condition?: (context: any) => boolean;
}

export const REDIRECT_RULES: RedirectRule[] = [
  // 루트 경로는 기본 언어 + feed로 리다이렉트
  {
    from: '/',
    to: (params) => `/ko/feed`
  },
  
  // 언어 루트 (예: /ko)는 feed로 리다이렉트  
  {
    from: /^\/[a-z]{2}$/,
    to: (params) => `${params.pathname}/feed`
  },
  
  // 언어 없는 모든 경로는 한국어(ko) 추가 (oauth2 제외)
  // 이 규칙이 /landing, /settings 등 모든 경로를 자동 처리
  {
    from: /^\/(?!(?:ko|en|ja|oauth2)\/).*$/,
    to: (params) => `/ko${params.pathname}${params.search}${params.hash}`
  }
];

// 언어 감지 설정
export const LANGUAGE_DETECTION_CONFIG = {
  storageKey: I18N_LANG_KEY, // 실제 상수 사용
  fallbackLanguage: DEFAULT_LANGUAGE,
  enableBrowserDetection: true,
  enableUrlDetection: true,
};